import { gql } from "@/gql";

/** Phase 1 – initial page load (no customer/org, no bill/ship, no licenses). */
export const PRICED_CONTRACT_INITIAL = gql(`
  query ContractInitial($id: ID!) {
    contract(id: $id) {
      ...PricedContractInitial
      id
    }
  }
`);

/** Customer + organization for contract detail page (by customer id). */
export const CONTRACT_PAGE_CUSTOMER = gql(`
  query ContractPageCustomer(
    $customerId: ID!
    $dealerAccountId: ID!
    $includeCustomerIamId: Boolean! = false
  ) {
    dealerCustomerById(
      customerId: $customerId
      dealerAccountId: $dealerAccountId
    ) {
      id
      companyName
      iamId @include(if: $includeCustomerIamId)
      optInStatus
      organization {
        id
        verified
        organization {
          id
          name
        }
      }
      ... on DealerCustomer @defer(label: "customerOwners") {
        users(where: { role: { eq: OWNER } }) {
          ... on User {
            role
          }
          ... on PendingUser {
            role
          }
        }
      }
    }
  }
`);

/** Phase 3 – Contract Details tab: bill/ship (deferred until tab open). */
export const PRICED_CONTRACT_DETAILS = gql(`
  query ContractDetails($id: ID!) {
    contract(id: $id) {
      ...PricedContractDetails
      id
    }
  }
`);

/** Phase 4 – license breakdown (parallel with phase 1 on mount). */
export const PRICED_CONTRACT_LICENSES = gql(`
  query ContractLicenses($id: ID!) {
    contract(id: $id) {
      ...PricedContractLicenses
      id
    }
  }
`);

/** Phase 5 – Renewal Planning / History: negative requests **/
export const PRICED_CONTRACT_NEGATIVE_REQUESTS = gql(`
  query ContractNegativeRequests($id: ID!) {
    contract(id: $id) {
      ...PricedContractNegativeRequests
      id
    }
  }
`);

/** Slim contract – amend-contract wizard page. */
export const PRICED_CONTRACT_AMEND = gql(`
  query ContractAmend($id: ID!) {
    contract(id: $id) {
      ...PricedContractAmend
    }
  }
`);

export const VALIDATE_CONTRACT_PRODUCTS_PRICING = gql(`
  query ValidateContractProductsPricing($contractId: ID!, $effectiveDate: DateTime!, $productIds: [String!]!) {
    validateContractProductsPricing(contractId: $contractId, effectiveDate: $effectiveDate, productIds: $productIds) {
      invalidProductIds
      isValid
    }
  }
`);

/** Bill/ship address and contact check before navigating to the amend wizard. */
export const CONTRACT_AMEND_DETAILS_CHECK = gql(`
  query ContractAmendDetailsCheck($id: ID!) {
    contract(id: $id) {
      id
      billToAddress {
        id
      }
      billToContact {
        id
      }
      shipToAddress {
        id
      }
      shipToContact {
        id
      }
    }
  }
`);

/** Full contract – mutations, refetch, sidesheet. */
export const PRICED_CONTRACT = gql(`
  query Contract($id: ID!) {
    contract(id: $id) {
      ...PricedContract
      id
    }
  }
`);

/** Lightweight contract refetch after mutations (isAmendable + license block status). */
export const CONTRACT_AMENDABLE_FLAG = gql(`
  query ContractAmendableFlag($id: ID!) {
    contract(id: $id) {
      id
      isAmendable
      licenseAssignmentStatus {
        blockedReason
        isBlocked
      }
    }
  }
`);

export const LIST_CONTRACTS = gql(`
  query Contracts($accountId: ID!, $after: String, $first: Int!, $includeCustomerIamId: Boolean! = false, $order: [ContractSortInput!], $where: ContractFilterInput) {
    contractsV2(accountId: $accountId, after: $after, first: $first, order: $order, where: $where) {
      hasUnFilteredResults
      totalCount
      nodes {
        ...ContractTableDetails
      }
    }
  }
`);

export const DEALER_DASHBOARD_EVENTS = gql(`
  query DealerDashboardEvents( $after: String, $before: String, $first: Int, $last: Int, $order: [AttentionEventSortInput!], $searchTerm: String, $where: DealerDashboardFilterInput) {
    dealerDashboardEvents(after: $after, before: $before, first: $first, last: $last, order: $order, searchTerm: $searchTerm, where: $where) {
      totalCount
      nodes {
        ...DealerDashboardEvents
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);

export const SELECTABLE_CUSTOMER_FRAGMENT = gql(`
  fragment SelectableCustomer on DealerCustomer {
    id
    companyName
    optInStatus
    organization {
      id
      verified
      organization {
        id
        name
      }
    }
    users(where: { role: { eq: OWNER } }) {
      ... on User {
        id
        email
        role
      }
      ... on PendingUser {
        id
        email
        role
      }
    }
  }
`);

export const DEALER_CUSTOMERS_PAGED = gql(`
  query DealerCustomersPaged(
    $accountId: ID!
    $after: String
    $first: Int!
    $where: DealerCustomerFilterInput
  ) {
    dealerCustomersPaged(
      accountId: $accountId
      after: $after
      first: $first
      where: $where
    ) {
      nodes {
        ...SelectableCustomer
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);

export const INVOICES = gql(`
  query Invoices(
    $after: String,
    $before: String, $dealerAccountId: String!, $first: Int, $includeCustomerIamId: Boolean! = false,
    $last: Int, $order: [DealerInvoiceSortInput!],
    $where: DealerInvoiceFilterInput
  ) {
    dealerInvoices(
      after: $after,
      before: $before, dealerAccountId: $dealerAccountId, first: $first, last: $last, order: $order, where: $where
    ) {
      totalCount
      nodes {
        id
        balance
        createdDate
        dueDate
        invoiceNumber
        paymentTerm
        totalAmountWithTax
        contract {
          id
          contractNumber
          contractNumberSearch
          customerName
          purchaseOrderNumber
          shipToAccountId
          currency {
            id
          }
          shipToAccount @include(if: $includeCustomerIamId) {
            id
            iamId
          }
        }
      }
    }
  }
`);

export const CREDIT_MEMOS = gql(`
  query CreditMemos($after: String, $before: String,
    $dealerAccountId: String!, $first: Int, $includeCustomerIamId: Boolean! = false,  $last: Int, $order: [CreditMemoSortInput!],
    $where: CreditMemoFilterInput) {
    creditMemos(after: $after, before: $before, dealerAccountId: $dealerAccountId, first: $first, last: $last, order: $order, where: $where) {
      totalCount
      nodes {
        id
        createdDate
        creditMemoNumber
        invoiceNumber
        totalAmountWithTax
        contract {
          id
          contractNumber
          contractNumberSearch
          customerName
          purchaseOrderNumber
          shipToAccountId
          currency{
            id
          }
          shipToAccount @include(if: $includeCustomerIamId) {
            id
            iamId
          }
        }
      }
    }
  }
`);

export const PRODUCT_PRICELIST_ACCOUNT_COUNTRIES = gql(`
  query ProductPricelistAccountCountries($id: ID!) {
    account(id: $id) {
      billToCountries {
        code
        isDefault
        name
      }
      currency {
        id
        description
      }
    }
  }
`);

export const REQUEST_PRODUCT_PRICE_LIST = gql(`
  mutation RequestProductPriceList($input: RequestProductPricelistInput!) {
    requestProductPriceList(input: $input)
  }
`);
